/**
 * The three `ReportData` fixtures every Intake report story is driven from.
 *
 * - `computedReportFixture` — the design's own content, verbatim, from
 *   `compass-oppreport.jsx` and `compass-assumptions.jsx` in Claude Design
 *   project `79357508-fe09-4122-831f-a0014c6a31b0`. Horizon 5, ROI 160%,
 *   payback "1.1 years", net savings $600K, total cost $375K.
 * - `pendingReportFixture` — the same report before the assumptions table is
 *   complete. Two values still need input, so every figure renders `—`.
 * - `partialReportFixture` — the honest picture of today's read model. Every
 *   field the missing-data register (plan §5) says we do not have is absent
 *   here, so the `Partial` stories show what the report actually looks like on
 *   real data before any backend work lands.
 *
 * Phase 1 owns this file. A §5 gap closing should be a one-line change here.
 *
 * ## How `traceNodes` is keyed
 *
 * Every entry's key is its `ReportTraceNode.id`, and ids are chosen so that a
 * formula token resolves with a plain `traceNodes[token]` lookup:
 *
 * - `"assumption"` / `"fact"` nodes — id is the human label the read model puts
 *   in `ReportStreamRow.formula` (`formulaDisplayTokens` emits labels, not ids).
 * - `"stream"` nodes — id is the stream's display name, because aggregate
 *   formulas reference streams by name. `ReportStreamRow.id` keeps the read
 *   model's stable slug and is deliberately *not* a key here.
 * - `"metric"` nodes — id is the matching `ReportMetricRow.id`.
 * - `"figure"` nodes — id is the `ReportFigureKey`, plus the two roll-up nodes
 *   (`"Total benefit over 5 years"` / `"Total cost over 5 years"`) that the
 *   design's aggregate formulas reference by label.
 *
 * No node object appears under two keys, so `Object.values(traceNodes)` is safe
 * to iterate.
 */

import type { SrcCardSource } from "../data-display/SrcCard";
import { REPORT_EMPTY_VALUE } from "./report-format";
import type {
  ReportComparableVendor,
  ReportData,
  ReportExecSummaryBullet,
  ReportMetricRow,
  ReportRationaleRow,
  ReportSource,
  ReportStreamRow,
  ReportTraceNode,
} from "./report-types";

/* ------------------------------------------------------------------ *
 * Sources — design `BIB_REPORT` entries and the assumption-row sources
 * ------------------------------------------------------------------ */

const ORG_PROFILE_PAYROLL: SrcCardSource = {
  name: "Elion · Organization profile",
  sub: "FY25 nursing payroll",
  year: "2026",
  kind: "org",
  used: true,
};

const ORG_PROFILE_SCHEDULE: SrcCardSource = {
  name: "Elion · Organization profile",
  sub: "Nursing schedule",
  year: "2026",
  kind: "org",
  used: true,
};

const ORG_PROFILE_SCOPE: SrcCardSource = {
  name: "Elion · Organization profile",
  sub: "Deployment scope",
  year: "2026",
  kind: "org",
  used: true,
};

const AIVA_PRICING_QUOTE: SrcCardSource = {
  name: "Aiva Health · Pricing quote",
  sub: "Enterprise agreement",
  year: "2026",
  kind: "org",
  used: true,
};

const AIVA_CASE_STUDY: SrcCardSource = {
  name: "Aiva Health · Cedars deployment case study",
  sub: "48-bed unit · 6-month deployment",
  year: "2025",
  kind: "external",
  url: "https://aivahealth.com/case-studies/cedars",
  used: true,
};

const NSI_RETENTION_REPORT: SrcCardSource = {
  name: "NSI · National health care retention report",
  sub: "National retention report · 2025",
  year: "2025",
  kind: "external",
  url: "https://www.nsinursingsolutions.com/documents/library/nsi_national_health_care_retention_report.pdf",
  used: true,
};

const ELION_BENCHMARK: SrcCardSource = {
  name: "Elion Benchmark · Ambient documentation outcomes",
  sub: "De-identified panel · 22 health systems",
  year: "2026",
  kind: "catalog",
  blurb:
    "Across reporting systems, ambient documentation reduced incidental nursing overtime by a median of 38%.",
  value: 38,
  valueLabel: "38%",
};

/**
 * The design's `BIB_REPORT`, entries 1-4, as `ReportSource`.
 *
 * `n` matches the `citation` numbers the computed fixture's problem,
 * highlights, risks and rationale rows already carry, so the `Computed`
 * stories show markers that land on the right entry.
 *
 * Two fields the design's own fixture does not have:
 *
 * - `dedupKey` — the identity a claim stores instead of a number. The design
 *   has no equivalent because its numbers are literals; these are shaped like
 *   the Argus `SourceCitation.id` values the real mapper dedupes on.
 * - `backsCount` — the design's `BacksModal` is dead code there (no entry
 *   carries `backs`), so the counts here are chosen to exercise both branches
 *   of the "Backs N values" control, including the entry that has none.
 */
const COMPUTED_SOURCES: ReportSource[] = [
  {
    n: 1,
    dedupKey: "amia-25x5-reducing-burden-elevating-care",
    title: "Reducing burden, elevating care: lessons from the 25x5 series",
    meta: "AMIA · 2025 · professional informatics body",
    context:
      "Case study of ambient documentation on a 48-bed unit at Cedars-Sinai, covering overtime, time per shift, and nurse sentiment. Reports 1,440 annual overtime hours before the intervention, 1.5 to 2 hours returned per shift, and 41% of nurses considering leaving within six months.",
    trust:
      "Peer-presented through a professional informatics body rather than vendor-authored. Strong, but drawn from a single 48-bed unit.",
    urls: [
      "https://amia.org/education-events/education-catalog/25x5-webinar-series-reducing-burden-elevating-care-lessons",
      "https://www.amia.org/25x5/clinician-burden-summary",
    ],
    internalLabel: null,
    backsCount: 3,
  },
  {
    n: 2,
    dedupKey: "aiva-health-ambient-documentation",
    title: "Ambient documentation: reported outcomes",
    meta: "Aiva Health · 2026 · vendor",
    context:
      "The vendor's own evidence page for the product under evaluation. Claims 81% faster documentation, 50% less incidental overtime, and 37% higher Press Ganey scores on nursing-specific questions.",
    trust:
      "Vendor-published and not independently audited. Treat as a ceiling, not an expectation.",
    urls: ["https://www.aivahealth.com/ambient-documentation"],
    internalLabel: null,
    backsCount: 2,
  },
  {
    n: 3,
    dedupKey: "nsi-national-health-care-retention-report",
    title: "National health care retention report",
    meta: "NSI Nursing Solutions · 2025 · industry research",
    context:
      "Annual national benchmark on nursing turnover rates and replacement cost per departure. Places nursing turnover cost at $4M to $6M annually for a typical hospital, and roughly $50,000 per nurse replaced.",
    trust:
      "Long-running national series, widely cited in nurse workforce planning. Strong for cost-per-turnover.",
    urls: [
      "https://www.nsinursingsolutions.com/documents/library/nsi_national_health_care_retention_report.pdf",
    ],
    internalLabel: null,
    backsCount: 1,
  },
  {
    n: 4,
    dedupKey: "riverbend-nursing-workforce-plan-fy26",
    title: "Nursing workforce plan, FY26 to FY28",
    meta: "Riverbend Health · internal · system of record: nursing office",
    context:
      "The health system's own workforce plan, naming documentation burden and incidental overtime as retention priorities. Establishes that this purchase advances an approved objective rather than opening a new initiative.",
    trust:
      "Internal and authoritative for intent, but not evidence of vendor performance.",
    urls: [],
    internalLabel: "Nursing office",
    // Strategy rather than a model input: it backs the narrative, not a value.
    backsCount: 0,
  },
];

/* ------------------------------------------------------------------ *
 * Computed — the design's own content
 * ------------------------------------------------------------------ */

const COMPUTED_STREAMS = {
  overtimeSavings: {
    id: "vs-overtime-savings",
    traceNodeId: "Overtime savings",
    name: "Overtime savings",
    direction: "benefit",
    cadence: "annual",
    amount: "$225K",
    description:
      "Takes today's overtime hours, applies the reduction Aiva is expected to deliver, and values the avoided hours at the loaded overtime rate.",
    formula: [
      "Annual nursing overtime hours baseline",
      "×",
      "Overtime hourly rate",
      "×",
      "Expected reduction in overtime",
    ],
    inputIds: [
      "c:nursing_overtime_hours",
      "c:overtime_hourly_rate",
      "a:overtime_reduction_pct",
    ],
  },
  nursingHoursSavings: {
    id: "vs-nursing-hours-savings",
    traceNodeId: "Nursing hours savings",
    name: "Nursing hours savings",
    direction: "benefit",
    cadence: "annual",
    amount: "$12K",
    description:
      "Multiplies hours saved on each shift by the number of shifts a nurse runs in a year, valued at the loaded nursing rate rather than the overtime rate.",
    formula: [
      "Shifts per year",
      "×",
      "Hours saved per shift",
      "×",
      "Value of nursing hours",
    ],
    inputIds: [
      "c:nursing_shifts_per_year",
      "a:hours_saved_per_shift",
      "c:nursing_hourly_value",
    ],
  },
  turnoverSavings: {
    id: "vs-turnover-savings",
    traceNodeId: "Turnover savings",
    name: "Turnover savings",
    direction: "benefit",
    cadence: "annual",
    amount: "$42K",
    description:
      "Sizes today's turnover from the nurses in scope and the annual turnover rate, prices it at the cost to replace one nurse, then applies the expected improvement.",
    formula: [
      "Nurses in scope",
      "×",
      "Annual nursing turnover rate",
      "×",
      "Cost to replace one nurse",
      "×",
      "Improvement in nursing turnover",
    ],
    inputIds: [
      "c:workforce_nurse",
      "a:nursing_turnover_rate",
      "a:nurse_replacement_cost",
      "a:turnover_improvement_pct",
    ],
  },
  subscriptionCost: {
    id: "vs-subscription-cost",
    traceNodeId: "Subscription cost",
    name: "Subscription cost",
    direction: "cost",
    cadence: "annual",
    amount: "$75K",
    description:
      "Combines the enterprise subscription with any ongoing infrastructure or implementation cost, and does not scale with the rollout ramp.",
    formula: ["Annual subscription fee", "+", "Annual implementation cost"],
    inputIds: ["a:annual_subscription_fee", "a:annual_implementation_cost"],
  },
  upfrontFees: {
    id: "vs-upfront-fees",
    traceNodeId: "Upfront fees",
    name: "Upfront fees",
    direction: "cost",
    cadence: "one-time",
    amount: "$0",
    description:
      "Combines Aiva's upfront platform fee with any one-time infrastructure or implementation work, and lands entirely in year one.",
    formula: ["Upfront Aiva fee", "+", "Upfront implementation fee"],
    inputIds: ["a:upfront_platform_fee", "a:upfront_implementation_fee"],
  },
} satisfies Record<string, ReportStreamRow>;

const COMPUTED_METRICS: ReportMetricRow[] = [
  {
    id: "m1",
    traceNodeId: "m1",
    label: "Overtime savings",
    description:
      "Annual dollars saved from incidental overtime that Aiva prevents. Takes today's overtime hours, applies the reduction Aiva is expected to deliver, and values the avoided hours at the loaded overtime rate.",
    amount: "$225K",
    amountValue: 225_000,
    cadence: "annual",
    shift: {
      label: "Overtime hours",
      from: "6,000",
      to: "3,000",
      unit: "hours / year",
      pct: "50%",
      dir: "down",
    },
    streams: [COMPUTED_STREAMS.overtimeSavings],
  },
  {
    id: "m2",
    traceNodeId: "m2",
    label: "Nursing hours savings",
    description:
      "Annual value of the time Aiva gives back to nursing. Multiplies hours saved on each shift by the number of shifts a nurse runs in a year, valued at the loaded nursing rate rather than the overtime rate.",
    amount: "$12K",
    amountValue: 12_000,
    cadence: "annual",
    shift: {
      label: "Hours returned per nurse",
      from: "0",
      to: "234",
      unit: "hours / year",
      pct: null,
      dir: "up",
    },
    streams: [COMPUTED_STREAMS.nursingHoursSavings],
  },
  {
    id: "m3",
    traceNodeId: "m3",
    label: "Turnover savings",
    description:
      "Annual savings from nurses staying longer. Sizes today's turnover from the nurses in scope and the annual turnover rate, prices it at the cost to replace one nurse, then applies the expected improvement.",
    amount: "$42K",
    amountValue: 42_000,
    cadence: "annual",
    shift: {
      label: "Nurses leaving each year",
      from: "84",
      to: "83",
      unit: "nurses / year",
      pct: "1%",
      dir: "down",
    },
    streams: [COMPUTED_STREAMS.turnoverSavings],
  },
  {
    id: "c2",
    traceNodeId: "c2",
    label: "Subscription cost",
    description:
      "Recurring cost for every year of the model. Combines the enterprise subscription with any ongoing infrastructure or implementation cost, and does not scale with the rollout ramp.",
    amount: "$75K",
    amountValue: 75_000,
    cadence: "annual",
    shift: null,
    streams: [COMPUTED_STREAMS.subscriptionCost],
  },
  {
    id: "c1",
    traceNodeId: "c1",
    label: "Upfront fees",
    description:
      "One-time cost due before the rollout begins. Combines Aiva's upfront platform fee with any one-time infrastructure or implementation work, and lands entirely in year one.",
    // The design's quote carries no upfront fee, so this row is genuinely $0.
    // It is still a one-time row: the design models `c1` as its own cost
    // metric with `cadence: "one-time"`, which is what populates the
    // One-time group.
    amount: "$0",
    amountValue: 0,
    cadence: "one-time",
    shift: null,
    streams: [COMPUTED_STREAMS.upfrontFees],
  },
];

const COMPUTED_EXEC_SUMMARY: ReportExecSummaryBullet[] = [
  {
    id: "what",
    label: "What.",
    body: [
      {
        kind: "text",
        text: "This opportunity targets nursing documentation in the inpatient setting.",
      },
    ],
    citation: null,
  },
  {
    id: "cost",
    label: "Cost.",
    body: [
      { kind: "text", text: "The model costs " },
      {
        kind: "ref",
        ref: { kind: "figure", key: "totalCost" },
        text: "$375K",
        traceNodeId: "totalCost",
      },
      {
        kind: "text",
        text: " over five years. Subscription only. No upfront fee.",
      },
    ],
    citation: null,
  },
  {
    id: "return",
    label: "Return.",
    body: [
      {
        kind: "text",
        text: "After adjusting benefits for the monthly rollout, net savings are ",
      },
      {
        kind: "ref",
        ref: { kind: "figure", key: "netSavings" },
        text: "$600K",
        traceNodeId: "netSavings",
      },
      { kind: "text", text: ", with payback in 1.1 years." },
    ],
    citation: null,
  },
  {
    id: "where-it-comes-from",
    label: "Where it comes from.",
    body: [
      { kind: "text", text: "The largest modeled benefit is " },
      {
        kind: "ref",
        ref: { kind: "metric", id: "m1", field: "label" },
        text: "Overtime savings",
        traceNodeId: "m1",
      },
      { kind: "text", text: "." },
    ],
    citation: null,
  },
  {
    id: "confidence",
    label: "Confidence.",
    body: [
      {
        kind: "text",
        text: "Confidence reflects the mix of available local data, research, and vendor evidence.",
      },
    ],
    citation: null,
  },
  {
    id: "softest-figure",
    label: "Softest figure.",
    body: [
      {
        kind: "text",
        text: "This is a judgement call: turnover attribution is the softest input in the model.",
      },
    ],
    citation: null,
  },
];

/**
 * An uncomputed projection, as `executiveSummaryDrafts` now builds it: one
 * statement in the `cost` slot and no `return` bullet. The per-ref pending copy
 * is written to stand in for a single absent number inside an otherwise
 * complete sentence, so with every figure absent it read "The model costs Still
 * modeling over a period still being modeled."
 */
const PENDING_EXEC_SUMMARY: ReportExecSummaryBullet[] = [
  {
    id: "cost",
    label: "Not yet projected.",
    body: [
      {
        kind: "text",
        text: "Cost, net savings and payback are not stated: the model has not been projected yet. Subscription only. No upfront fee.",
      },
    ],
    citation: null,
  },
];

const COMPUTED_RATIONALE: ReportRationaleRow[] = [
  {
    id: "why-vendor",
    label: "Why this vendor",
    body: "Aiva is the only shortlisted product with a referenceable inpatient nursing deployment rather than physician-side evidence alone, and it is priced per deployment rather than per clinician, which matters against a 400-nurse footprint.",
    citation: 1,
  },
  {
    id: "why-now",
    label: "Why now",
    body: "The workforce plan already commits the system to relieving documentation burden this cycle, and the quote carries no upfront fee, so the decision does not compete for capital budget.",
    citation: 4,
  },
  {
    id: "why-fits",
    label: "Why it fits this organization",
    body: "The benefit model rests on incidental overtime, which this system already tracks and can verify against payroll. That makes the primary driver auditable after go-live rather than a claim taken on faith.",
    citation: null,
  },
];

const COMPUTED_COMPS: ReportComparableVendor[] = [
  {
    id: "abridge",
    name: "Abridge",
    category: "Ambient documentation",
    reasonsNotSelected: [
      "No inpatient nursing deployment at comparable scale to reference.",
      "Evidence is strongest on the physician side; the nursing workflow is newer.",
    ],
  },
  {
    id: "nuance-dax",
    name: "Nuance DAX",
    category: "Ambient documentation",
    reasonsNotSelected: [
      "Priced per clinician, which scales poorly against a 400-nurse footprint.",
      "Quoted materially higher than the shortlisted option.",
    ],
  },
  {
    id: "suki",
    name: "Suki",
    category: "Ambient documentation",
    reasonsNotSelected: [
      "Deployment model assumes ambulatory encounters.",
      "Inpatient shift charting would need custom work.",
    ],
  },
  {
    id: "keep-current-process",
    name: "Keep current process",
    category: "Baseline",
    reasonsNotSelected: [
      "Leaves the overtime cost in place.",
      "Does not address a retention risk the system already committed to relieving.",
    ],
  },
];

const COMPUTED_KNOWN_INPUTS = [
  { id: "Total benefit over 5 years", label: "Total benefit", value: "$975K" },
  { id: "Total cost over 5 years", label: "Total cost", value: "$375K" },
  {
    id: "Annual subscription fee",
    label: "Annual subscription fee",
    value: "$75K",
  },
];

const COMPUTED_CONTRIBUTORS = [
  {
    id: "Overtime savings",
    label: "Overtime savings",
    value: "$225K",
    cadence: "annual" as const,
    formula: COMPUTED_STREAMS.overtimeSavings.formula,
  },
  {
    id: "Nursing hours savings",
    label: "Nursing hours savings",
    value: "$12K",
    cadence: "annual" as const,
    formula: COMPUTED_STREAMS.nursingHoursSavings.formula,
  },
  {
    id: "Turnover savings",
    label: "Turnover savings",
    value: "$42K",
    cadence: "annual" as const,
    formula: COMPUTED_STREAMS.turnoverSavings.formula,
  },
  {
    id: "Subscription cost",
    label: "Subscription cost",
    value: "$75K",
    cadence: "annual" as const,
    formula: COMPUTED_STREAMS.subscriptionCost.formula,
  },
  {
    id: "Upfront fees",
    label: "Upfront fees",
    value: "$0",
    cadence: "one-time" as const,
    formula: COMPUTED_STREAMS.upfrontFees.formula,
  },
];

function streamNode(
  stream: ReportStreamRow,
  extra: Partial<ReportTraceNode> = {},
): ReportTraceNode {
  return {
    id: stream.traceNodeId,
    kind: "stream",
    label: stream.name,
    value: stream.amount,
    unit: stream.cadence === "annual" ? "per year" : "one time",
    description: stream.description,
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    // A stream node always carries its row's own breakdown, so selecting it
    // inside an aggregate card shows how it is calculated.
    formula: stream.formula,
    aggregate: null,
    ...extra,
  };
}

const ROI_FORMULA = [
  "(",
  "Total benefit over 5 years",
  "−",
  "Total cost over 5 years",
  ")",
  "÷",
  "Total cost over 5 years",
];
const NET_SAVINGS_FORMULA = [
  "Total benefit over 5 years",
  "−",
  "Total cost over 5 years",
];
const PAYBACK_FORMULA = [
  "Total cost over 5 years",
  "÷",
  "(",
  "Total benefit over 5 years",
  "÷",
  "5 years",
  ")",
];
const TOTAL_COST_FORMULA = [
  "Subscription cost",
  "×",
  "5 years",
  "+",
  "Upfront fees",
];

const COMPUTED_TRACE_NODES: Record<string, ReportTraceNode> = {
  /* ---- figures ---- */
  roi: {
    id: "roi",
    kind: "figure",
    label: "Return on investment",
    value: "160%",
    unit: null,
    description: "Return over 5 years against total cost of ownership.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: ROI_FORMULA,
    aggregate: {
      horizonLabel: "5 years",
      horizonAssumed: false,
      contributors: COMPUTED_CONTRIBUTORS,
      knownInputs: COMPUTED_KNOWN_INPUTS,
      formula: ROI_FORMULA,
    },
  },
  netSavings: {
    id: "netSavings",
    kind: "figure",
    label: "Net 5-year savings",
    value: "$600K",
    unit: null,
    description: "Total rollout-adjusted benefit minus all program costs.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: NET_SAVINGS_FORMULA,
    aggregate: {
      horizonLabel: "5 years",
      horizonAssumed: false,
      contributors: COMPUTED_CONTRIBUTORS,
      knownInputs: COMPUTED_KNOWN_INPUTS,
      formula: NET_SAVINGS_FORMULA,
    },
  },
  payback: {
    id: "payback",
    kind: "figure",
    label: "Payback period",
    value: "1.1 years",
    unit: null,
    description: "Time until cumulative benefit covers cumulative cost.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: PAYBACK_FORMULA,
    aggregate: {
      horizonLabel: "5 years",
      horizonAssumed: false,
      contributors: COMPUTED_CONTRIBUTORS,
      knownInputs: COMPUTED_KNOWN_INPUTS,
      formula: PAYBACK_FORMULA,
    },
  },
  totalCost: {
    id: "totalCost",
    kind: "figure",
    label: "Total cost over 5 years",
    value: "$375K",
    unit: null,
    description:
      "Subscription and implementation across the evaluation window.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: TOTAL_COST_FORMULA,
    aggregate: {
      horizonLabel: "5 years",
      horizonAssumed: false,
      contributors: [
        {
          id: "Subscription cost",
          label: "Subscription cost",
          value: "$75K",
          cadence: "annual",
          formula: COMPUTED_STREAMS.subscriptionCost.formula,
        },
        {
          id: "Upfront fees",
          label: "Upfront fees",
          value: "$0",
          cadence: "one-time",
          formula: COMPUTED_STREAMS.upfrontFees.formula,
        },
      ],
      knownInputs: COMPUTED_KNOWN_INPUTS,
      formula: TOTAL_COST_FORMULA,
    },
  },

  /* ---- roll-ups the aggregate formulas reference by label ---- */
  "Total benefit over 5 years": {
    id: "Total benefit over 5 years",
    kind: "figure",
    label: "Total benefit over 5 years",
    value: "$975K",
    unit: null,
    description:
      "Every annual benefit roll-up, weighted by how much of the scope is live each year.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: null,
    aggregate: {
      horizonLabel: "5 years",
      horizonAssumed: false,
      contributors: [
        {
          id: "Overtime savings",
          label: "Overtime savings",
          value: "$225K",
          cadence: "annual",
          formula: COMPUTED_STREAMS.overtimeSavings.formula,
        },
        {
          id: "Nursing hours savings",
          label: "Nursing hours savings",
          value: "$12K",
          cadence: "annual",
          formula: COMPUTED_STREAMS.nursingHoursSavings.formula,
        },
        {
          id: "Turnover savings",
          label: "Turnover savings",
          value: "$42K",
          cadence: "annual",
          formula: COMPUTED_STREAMS.turnoverSavings.formula,
        },
      ],
      knownInputs: [
        {
          id: "annual-benefit",
          label: "Annual benefit at full scope",
          value: "$279K",
        },
        { id: "ramp", label: "Scope-weighted years live", value: "3.5" },
      ],
      formula: null,
    },
  },
  "Total cost over 5 years": {
    id: "Total cost over 5 years",
    kind: "figure",
    label: "Total cost over 5 years",
    value: "$375K",
    unit: null,
    description: "Subscription across the window plus any one-time fees.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: null,
    aggregate: {
      horizonLabel: "5 years",
      horizonAssumed: false,
      contributors: [
        {
          id: "Subscription cost",
          label: "Subscription cost",
          value: "$75K",
          cadence: "annual",
          formula: COMPUTED_STREAMS.subscriptionCost.formula,
        },
        {
          id: "Upfront fees",
          label: "Upfront fees",
          value: "$0",
          cadence: "one-time",
          formula: COMPUTED_STREAMS.upfrontFees.formula,
        },
      ],
      knownInputs: [
        { id: "annual-cost", label: "Annual cost", value: "$75K" },
        { id: "horizon", label: "Evaluation horizon", value: "5 years" },
      ],
      formula: null,
    },
  },

  /* ---- metrics ---- */
  m1: {
    id: "m1",
    kind: "metric",
    label: "Overtime savings",
    value: "$225K",
    unit: "per year",
    description:
      "Annual dollars saved from incidental overtime that Aiva prevents.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: COMPUTED_STREAMS.overtimeSavings.formula,
    aggregate: {
      horizonLabel: "5 years",
      horizonAssumed: false,
      contributors: [
        {
          id: "Overtime savings",
          label: "Overtime savings",
          value: "$225K",
          cadence: "annual",
          formula: COMPUTED_STREAMS.overtimeSavings.formula,
        },
      ],
      knownInputs: [
        {
          id: "Annual nursing overtime hours baseline",
          label: "Annual nursing overtime hours baseline",
          value: "6,000",
        },
        {
          id: "Overtime hourly rate",
          label: "Overtime hourly rate",
          value: "$75",
        },
      ],
      formula: COMPUTED_STREAMS.overtimeSavings.formula,
    },
  },
  m2: {
    id: "m2",
    kind: "metric",
    label: "Nursing hours savings",
    value: "$12K",
    unit: "per year",
    description: "Annual value of the time Aiva gives back to nursing.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: COMPUTED_STREAMS.nursingHoursSavings.formula,
    aggregate: {
      horizonLabel: "5 years",
      horizonAssumed: false,
      contributors: [
        {
          id: "Nursing hours savings",
          label: "Nursing hours savings",
          value: "$12K",
          cadence: "annual",
          formula: COMPUTED_STREAMS.nursingHoursSavings.formula,
        },
      ],
      knownInputs: [
        { id: "Shifts per year", label: "Shifts per year", value: "156" },
        {
          id: "Hours saved per shift",
          label: "Hours saved per shift",
          value: "1.5",
        },
      ],
      formula: COMPUTED_STREAMS.nursingHoursSavings.formula,
    },
  },
  m3: {
    id: "m3",
    kind: "metric",
    label: "Turnover savings",
    value: "$42K",
    unit: "per year",
    description: "Annual savings from nurses staying longer.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: COMPUTED_STREAMS.turnoverSavings.formula,
    aggregate: {
      horizonLabel: "5 years",
      horizonAssumed: false,
      contributors: [
        {
          id: "Turnover savings",
          label: "Turnover savings",
          value: "$42K",
          cadence: "annual",
          formula: COMPUTED_STREAMS.turnoverSavings.formula,
        },
      ],
      knownInputs: [
        { id: "Nurses in scope", label: "Nurses in scope", value: "400" },
        {
          id: "Cost to replace one nurse",
          label: "Cost to replace one nurse",
          value: "$50K",
        },
      ],
      formula: COMPUTED_STREAMS.turnoverSavings.formula,
    },
  },
  c2: {
    id: "c2",
    kind: "metric",
    label: "Subscription cost",
    value: "$75K",
    unit: "per year",
    description: "Recurring cost for every year of the model.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: COMPUTED_STREAMS.subscriptionCost.formula,
    aggregate: {
      horizonLabel: "5 years",
      horizonAssumed: false,
      contributors: [
        {
          id: "Subscription cost",
          label: "Subscription cost",
          value: "$75K",
          cadence: "annual",
          formula: COMPUTED_STREAMS.subscriptionCost.formula,
        },
      ],
      knownInputs: [
        {
          id: "Annual subscription fee",
          label: "Annual subscription fee",
          value: "$75K",
        },
      ],
      formula: COMPUTED_STREAMS.subscriptionCost.formula,
    },
  },
  c1: {
    id: "c1",
    kind: "metric",
    label: "Upfront fees",
    value: "$0",
    unit: "one time",
    description: "One-time cost due before the rollout begins.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: COMPUTED_STREAMS.upfrontFees.formula,
    aggregate: {
      horizonLabel: "5 years",
      horizonAssumed: false,
      contributors: [
        {
          id: "Upfront fees",
          label: "Upfront fees",
          value: "$0",
          cadence: "one-time",
          formula: COMPUTED_STREAMS.upfrontFees.formula,
        },
      ],
      knownInputs: [
        { id: "Upfront Aiva fee", label: "Upfront Aiva fee", value: "$0" },
        {
          id: "Upfront implementation fee",
          label: "Upfront implementation fee",
          value: "$0",
        },
      ],
      formula: COMPUTED_STREAMS.upfrontFees.formula,
    },
  },

  /* ---- streams ---- */
  "Overtime savings": streamNode(COMPUTED_STREAMS.overtimeSavings),
  "Nursing hours savings": streamNode(COMPUTED_STREAMS.nursingHoursSavings),
  "Turnover savings": streamNode(COMPUTED_STREAMS.turnoverSavings),
  "Subscription cost": streamNode(COMPUTED_STREAMS.subscriptionCost),
  "Upfront fees": streamNode(COMPUTED_STREAMS.upfrontFees),

  /* ---- facts (organization profile) ---- */
  "Annual nursing overtime hours baseline": {
    id: "Annual nursing overtime hours baseline",
    kind: "fact",
    label: "Annual nursing overtime hours baseline",
    value: "6,000",
    unit: "hours per year",
    description:
      "Incidental overtime hours nursing works today, before Aiva. The base of the overtime benefit.",
    provenance: "provided",
    sources: [ORG_PROFILE_PAYROLL],
    snippet:
      "Annual incidental overtime hours across the 200-bed deployment scope.",
    history: [
      {
        at: "March 14, 2026",
        actorLabel: "Dana Whitfield",
        from: "Needs input",
        to: "6,000",
        note: "Confirmed at intake.",
      },
    ],
    formula: null,
    aggregate: null,
  },
  "Overtime hourly rate": {
    id: "Overtime hourly rate",
    kind: "fact",
    label: "Overtime hourly rate",
    value: "$75",
    unit: "per hour",
    description: "What one overtime hour costs, loaded.",
    provenance: "provided",
    sources: [ORG_PROFILE_PAYROLL],
    snippet: "Loaded overtime rate for nursing staff in scope.",
    history: [
      {
        at: "March 14, 2026",
        actorLabel: "Dana Whitfield",
        from: "Needs input",
        to: "$75",
        note: "Confirmed at intake.",
      },
    ],
    formula: null,
    aggregate: null,
  },
  "Shifts per year": {
    id: "Shifts per year",
    kind: "fact",
    label: "Shifts per year",
    value: "156",
    unit: "shifts per year",
    description: "How many nursing shifts run in a year.",
    provenance: "provided",
    sources: [ORG_PROFILE_SCHEDULE],
    snippet: "Three 12-hour shifts per week, about 12 to 13 shifts a month.",
    history: [
      {
        at: "March 14, 2026",
        actorLabel: "Dana Whitfield",
        from: "Needs input",
        to: "156",
        note: "Confirmed at intake.",
      },
    ],
    formula: null,
    aggregate: null,
  },
  "Value of nursing hours": {
    id: "Value of nursing hours",
    kind: "fact",
    label: "Value of nursing hours",
    value: "$50",
    unit: "per hour",
    description: "What one regular nursing hour is worth, loaded.",
    provenance: "provided",
    sources: [ORG_PROFILE_PAYROLL],
    snippet:
      "Base loaded hourly rate for nursing staff, distinct from the overtime rate.",
    history: [],
    formula: null,
    aggregate: null,
  },
  "Nurses in scope": {
    id: "Nurses in scope",
    kind: "fact",
    label: "Nurses in scope",
    value: "400",
    unit: "nurses",
    description: "Nursing staff inside the deployment scope.",
    provenance: "provided",
    sources: [ORG_PROFILE_SCOPE],
    snippet:
      "Added during the intake step. Nursing staff covered by the deployment scope.",
    history: [
      {
        at: "March 14, 2026",
        actorLabel: "Russ Smith",
        from: "Needs input",
        to: "400",
        note: "Captured at intake.",
      },
    ],
    formula: null,
    aggregate: null,
  },

  /* ---- assumptions ---- */
  "Hours saved per shift": {
    id: "Hours saved per shift",
    kind: "assumption",
    label: "Hours saved per shift",
    value: "1.5",
    unit: "hours per shift",
    description:
      "Time returned to a nurse each shift once Aiva handles routine traffic.",
    provenance: "estimated",
    sources: [
      {
        ...AIVA_CASE_STUDY,
        blurb:
          "Nurses reported roughly 1.5 hours per shift returned to direct patient care after ambient documentation was deployed.",
      },
    ],
    snippet:
      "Nurses reported roughly 1.5 hours per shift returned to direct patient care after ambient documentation was deployed.",
    history: [],
    formula: null,
    aggregate: null,
  },
  "Annual nursing turnover rate": {
    id: "Annual nursing turnover rate",
    kind: "assumption",
    label: "Annual nursing turnover rate",
    value: "21%",
    unit: "of nurses",
    description: "Share of nursing staff who leave in a year, before Aiva.",
    provenance: "estimated",
    sources: [
      {
        ...NSI_RETENTION_REPORT,
        blurb:
          "The average hospital turned over 20.7% of its bedside nursing staff in the reporting year.",
      },
    ],
    snippet:
      "The average hospital turned over 20.7% of its bedside nursing staff in the reporting year.",
    history: [],
    formula: null,
    aggregate: null,
  },
  "Cost to replace one nurse": {
    id: "Cost to replace one nurse",
    kind: "assumption",
    label: "Cost to replace one nurse",
    value: "$50K",
    unit: "per nurse",
    description: "What it costs to recruit and ramp one replacement nurse.",
    provenance: "estimated",
    sources: [
      {
        ...NSI_RETENTION_REPORT,
        blurb:
          "The average cost of turnover for one bedside RN is $56,300, with recruiting, orientation, and lost productivity making up the bulk.",
      },
    ],
    snippet:
      "The average cost of turnover for one bedside RN is $56,300, with recruiting, orientation, and lost productivity making up the bulk.",
    history: [],
    formula: null,
    aggregate: null,
  },
  "Expected reduction in overtime": {
    id: "Expected reduction in overtime",
    kind: "assumption",
    label: "Expected reduction in overtime",
    value: "50%",
    unit: "of hours",
    description:
      "How much of the addressable overtime Aiva actually removes. The case study reports 50%, flagged to verify against internal overtime tracking.",
    provenance: "estimated",
    sources: [
      {
        ...AIVA_CASE_STUDY,
        blurb:
          "Incidental overtime on the pilot unit fell roughly 50% within three months of go-live.",
        value: 50,
        valueLabel: "50%",
      },
      ELION_BENCHMARK,
    ],
    snippet:
      "The Cedars deployment saw incidental overtime fall by about half once Aiva absorbed routine call-light traffic.",
    history: [
      {
        at: "March 14, 2026",
        actorLabel: "Russ Smith",
        from: "Needs input",
        to: "50%",
        note: "Adopted the Cedars case-study figure.",
      },
    ],
    formula: null,
    aggregate: null,
  },
  "Improvement in nursing turnover": {
    id: "Improvement in nursing turnover",
    kind: "assumption",
    label: "Improvement in nursing turnover",
    value: "1%",
    unit: "of turnover cost",
    description:
      "How much annual nursing turnover improves. Not supported by the case study or Aiva's materials, so this is a judgement call.",
    provenance: "estimated",
    sources: [
      {
        ...NSI_RETENTION_REPORT,
        blurb:
          "Workload and documentation burden rank among the top drivers of nursing departure, though attribution to any single tool is rarely isolated.",
        value: 1,
        valueLabel: "1%",
      },
    ],
    snippet:
      "No vendor-reported figure exists for this. The 1% used in the source model was the author's own conservative estimate.",
    history: [
      {
        at: "March 14, 2026",
        actorLabel: "Russ Smith",
        from: "Needs input",
        to: "1%",
        note: "Deliberately conservative judgement call.",
      },
    ],
    formula: null,
    aggregate: null,
  },
  "Upfront Aiva fee": {
    id: "Upfront Aiva fee",
    kind: "assumption",
    label: "Upfront Aiva fee",
    value: "$0",
    unit: "one time",
    description: "One-time platform fee due at signature.",
    provenance: "provided",
    sources: [AIVA_PRICING_QUOTE],
    snippet: "No upfront platform fee in the current quote.",
    history: [],
    formula: null,
    aggregate: null,
  },
  "Upfront implementation fee": {
    id: "Upfront implementation fee",
    kind: "assumption",
    label: "Upfront implementation fee",
    value: "$0",
    unit: "one time",
    description: "One-time infrastructure or implementation cost.",
    provenance: "provided",
    sources: [AIVA_PRICING_QUOTE],
    snippet: "No upfront infrastructure or implementation cost quoted.",
    history: [],
    formula: null,
    aggregate: null,
  },
  "Annual subscription fee": {
    id: "Annual subscription fee",
    kind: "assumption",
    label: "Annual subscription fee",
    value: "$75K",
    unit: "per year",
    description: "Recurring subscription fee for each year of the model.",
    provenance: "provided",
    sources: [AIVA_PRICING_QUOTE],
    snippet: "Annual enterprise subscription fee.",
    history: [
      {
        at: "March 14, 2026",
        actorLabel: "Marcus Hale",
        from: "Needs input",
        to: "$75K",
        note: "Confirmed at intake.",
      },
    ],
    formula: null,
    aggregate: null,
  },
  "Annual implementation cost": {
    id: "Annual implementation cost",
    kind: "assumption",
    label: "Annual implementation cost",
    value: "$0",
    unit: "per year",
    description: "Recurring infrastructure or implementation cost.",
    provenance: "provided",
    sources: [AIVA_PRICING_QUOTE],
    snippet: "No ongoing infrastructure or implementation cost quoted.",
    history: [],
    formula: null,
    aggregate: null,
  },
};

/** Every field present — the design's own content, verbatim. */
export const computedReportFixture: ReportData = {
  locked: false,
  needsInputCount: 0,
  projectionStatus: "computed",
  problem: {
    statement:
      "Nursing documentation burden is driving avoidable incidental overtime and contributing to turnover on inpatient units.",
    detail:
      "Charting spills past the end of shift, so nurses stay late to finish it. That overtime is unbudgeted, and the documentation load is named in the system's own workforce plan as a retention risk.",
    citation: 4,
  },
  figures: {
    ready: true,
    horizonYears: 5,
    horizonAssumed: false,
    roiPercent: 160,
    netSavings: 600450,
    paybackLabel: "1.1 years",
    totalCost: 375000,
  },
  metrics: COMPUTED_METRICS,
  leafNodeKeys: {},
  execSummary: COMPUTED_EXEC_SUMMARY,
  highlights: [
    {
      text: "Overtime reduction is the dominant driver and the best-evidenced benefit in the model.",
      citation: 1,
    },
    {
      text: "No upfront fee and no recurring implementation charge, so cost is a single predictable line.",
      citation: 2,
    },
    {
      text: "Advances an already-approved workforce objective rather than opening a new initiative.",
      citation: 4,
    },
  ],
  risks: [
    {
      text: "The strongest evidence comes from a single 48-bed unit; this deployment covers 200 beds.",
      citation: 1,
    },
    {
      text: "Turnover attribution is the softest figure in the model and is flagged as a judgement call.",
      citation: 3,
    },
    {
      text: "Vendor outcome claims are self-published and not independently audited.",
      citation: 2,
    },
    {
      text: "Benefit is time and retention, not new revenue, so it needs budget discipline to realize.",
      citation: null,
    },
  ],
  scope: {
    // The design's own pill line — "200 inpatient beds · 400 nurses" — so both
    // tiles are quantitative and both are in the pill.
    tiles: [
      {
        id: "inpatient-beds",
        label: "Inpatient beds",
        value: "200",
        origin: "deployment",
        inPill: true,
      },
      {
        id: "nurses-in-scope",
        label: "Nurses in scope",
        value: "400",
        origin: "deployment",
        inPill: true,
      },
    ],
    outOfScope:
      "Ambulatory clinics, the emergency department, and physician documentation are out of scope and treated as phase-2 candidates.",
    attribution: { capturedBy: "Russ Smith", capturedAt: "March 14, 2026" },
  },
  rationale: COMPUTED_RATIONALE,
  comparableVendors: COMPUTED_COMPS,
  sources: COMPUTED_SOURCES,
  traceNodes: COMPUTED_TRACE_NODES,
};

/* ------------------------------------------------------------------ *
 * Pending — the same report before the assumptions table is complete
 * ------------------------------------------------------------------ */

/** The two rows the design leaves empty, so `CS.missing()` returns 2. */
const PENDING_UNANSWERED = new Set([
  "Expected reduction in overtime",
  "Improvement in nursing turnover",
]);

function pendingStream(stream: ReportStreamRow): ReportStreamRow {
  return { ...stream, amount: REPORT_EMPTY_VALUE };
}

function pendingMetric(metric: ReportMetricRow): ReportMetricRow {
  return {
    ...metric,
    amount: REPORT_EMPTY_VALUE,
    amountValue: null,
    // The design's shift numbers come from `evalFormula`, which returns null
    // while any referenced row is still empty — so no shift resolves either.
    shift: null,
    streams: metric.streams.map(pendingStream),
  };
}

function pendingNode(node: ReportTraceNode): ReportTraceNode {
  if (node.aggregate) {
    return {
      ...node,
      value: null,
      aggregate: {
        ...node.aggregate,
        contributors: node.aggregate.contributors.map((contributor) => ({
          ...contributor,
          value: REPORT_EMPTY_VALUE,
        })),
        knownInputs: node.aggregate.knownInputs.map((input) =>
          PENDING_UNANSWERED.has(input.id)
            ? { ...input, value: REPORT_EMPTY_VALUE }
            : input,
        ),
      },
    };
  }
  if (node.kind === "stream") {
    return { ...node, value: REPORT_EMPTY_VALUE };
  }
  if (PENDING_UNANSWERED.has(node.id)) {
    return { ...node, value: null, provenance: "empty", history: [] };
  }
  return node;
}

const PENDING_TRACE_NODES: Record<string, ReportTraceNode> = Object.fromEntries(
  Object.entries(COMPUTED_TRACE_NODES).map(([key, node]) => [
    key,
    pendingNode(node),
  ]),
);

/**
 * `projectionStatus: "uncomputed"`, locked, two assumptions still open.
 *
 * `sources` is inherited from the computed fixture on purpose: the evidence a
 * model rests on does not change while its projection is incomplete, so the
 * bibliography is one of the few sections that reads identically in both
 * states.
 */
export const pendingReportFixture: ReportData = {
  ...computedReportFixture,
  locked: true,
  needsInputCount: 2,
  projectionStatus: "uncomputed",
  figures: {
    ready: false,
    horizonYears: null,
    horizonAssumed: false,
    roiPercent: null,
    netSavings: null,
    paybackLabel: null,
    totalCost: null,
  },
  metrics: COMPUTED_METRICS.map(pendingMetric),
  execSummary: PENDING_EXEC_SUMMARY,
  traceNodes: PENDING_TRACE_NODES,
};

/** Seven resolved slots, representing a valid persisted prose artifact. */
export const artifactBackedReportFixture: ReportData = {
  ...computedReportFixture,
  execSummary: [
    COMPUTED_EXEC_SUMMARY[0] as ReportExecSummaryBullet,
    {
      id: "why-now",
      label: "Why now.",
      body: [
        {
          kind: "text",
          text: "The workforce plan makes this the right moment to evaluate the change.",
        },
      ],
      citation: 4,
    },
    ...COMPUTED_EXEC_SUMMARY.slice(1),
  ],
};

/**
 * A missing-identity Cost artifact was dropped and deterministically
 * backfilled; the independent Why-now artifact claim survived resolution.
 */
export const degradedReportFixture: ReportData = {
  ...computedReportFixture,
  execSummary: [
    {
      id: "why-now",
      label: "Why now.",
      body: [
        {
          kind: "text",
          text: "This artifact claim survived live resolution; the missing-identity Cost claim was dropped and backfilled below.",
        },
      ],
      citation: null,
    },
    COMPUTED_EXEC_SUMMARY[1] as ReportExecSummaryBullet,
  ],
};

/**
 * A stored Return claim rested on a contaminated figure and was dropped; the
 * deterministic Return bullet backfilled the slot with the refusal that names
 * the offending input. Refusal is per bullet — the rest of the summary stands.
 */
export const degradedUnsafeReportFixture: ReportData = {
  ...computedReportFixture,
  execSummary: [
    COMPUTED_EXEC_SUMMARY[0] as ReportExecSummaryBullet,
    COMPUTED_EXEC_SUMMARY[1] as ReportExecSummaryBullet,
    {
      id: "return",
      label: "Return.",
      body: [
        {
          kind: "text",
          text: "Net savings and payback are not stated: Expected reduction in overtime is a proportion outside the 0–1 range, so every figure derived from it is outside the range the model can produce.",
        },
      ],
      citation: null,
    },
  ],
};

/* ------------------------------------------------------------------ *
 * Partial — the honest picture of today's read model
 * ------------------------------------------------------------------ */

const PARTIAL_STREAMS = {
  overtimeSavings: {
    id: "overtime-savings",
    traceNodeId: "Overtime savings",
    name: "Overtime savings",
    direction: "benefit",
    cadence: "annual",
    amount: "$225K",
    description:
      "Avoided incidental overtime hours valued at the loaded overtime rate.",
    formula: [
      "Annual nursing overtime hours baseline",
      "×",
      "Overtime hourly rate",
      "×",
      "Expected reduction in overtime",
    ],
    inputIds: [
      "c:nursing_overtime_hours",
      "c:overtime_hourly_rate",
      "a:overtime_reduction_pct",
    ],
  },
  documentationTimeReturned: {
    id: "documentation-time-returned",
    traceNodeId: "Documentation time returned",
    name: "Documentation time returned",
    direction: "benefit",
    cadence: "annual",
    amount: "$12K",
    description:
      "Charting time returned each shift, valued at the loaded nursing rate.",
    formula: [
      "Shifts per year",
      "×",
      "Hours saved per shift",
      "×",
      "Value of nursing hours",
    ],
    inputIds: [
      "c:nursing_shifts_per_year",
      "a:hours_saved_per_shift",
      "c:nursing_hourly_value",
    ],
  },
  turnoverAvoidance: {
    id: "turnover-avoidance",
    traceNodeId: "Turnover avoidance",
    name: "Turnover avoidance",
    direction: "benefit",
    cadence: "annual",
    amount: "$42K",
    description:
      "Replacement cost avoided when fewer nurses leave in a given year.",
    formula: [
      "Nurses in scope",
      "×",
      "Annual nursing turnover rate",
      "×",
      "Cost to replace one nurse",
      "×",
      "Improvement in nursing turnover",
    ],
    inputIds: [
      "c:workforce_nurse",
      "a:nursing_turnover_rate",
      "a:nurse_replacement_cost",
      "a:turnover_improvement_pct",
    ],
  },
  subscriptionCost: {
    id: "platform-subscription",
    traceNodeId: "Platform subscription",
    name: "Platform subscription",
    direction: "cost",
    cadence: "annual",
    amount: "$75K",
    description: "Recurring enterprise subscription for every modelled year.",
    formula: ["Annual subscription fee"],
    inputIds: ["a:annual_subscription_fee"],
  },
  implementationServices: {
    id: "implementation-services",
    traceNodeId: "Implementation services",
    name: "Implementation services",
    direction: "cost",
    cadence: "one-time",
    amount: "$40K",
    description:
      "Front-loaded integration and enablement work, billed once at go-live.",
    formula: ["Implementation services fee"],
    inputIds: ["a:implementation_services_fee"],
  },
} satisfies Record<string, ReportStreamRow>;

const PARTIAL_TRACE_NODES: Record<string, ReportTraceNode> = {
  /* ---- figures: only ROI resolves today (§5 #1–#4) ---- */
  roi: {
    id: "roi",
    kind: "figure",
    label: "Return on investment",
    value: "160%",
    unit: null,
    description: "Return against total cost of ownership.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: null,
    aggregate: {
      horizonLabel: "3 years",
      horizonAssumed: true,
      contributors: [
        {
          id: "Overtime savings",
          label: "Overtime savings",
          value: "$225K",
          cadence: "annual",
          formula: PARTIAL_STREAMS.overtimeSavings.formula,
        },
        {
          id: "Documentation time returned",
          label: "Documentation time returned",
          value: "$12K",
          cadence: "annual",
          formula: PARTIAL_STREAMS.documentationTimeReturned.formula,
        },
        {
          id: "Turnover avoidance",
          label: "Turnover avoidance",
          value: "$42K",
          cadence: "annual",
          formula: PARTIAL_STREAMS.turnoverAvoidance.formula,
        },
        {
          id: "Platform subscription",
          label: "Platform subscription",
          value: "$75K",
          cadence: "annual",
          formula: PARTIAL_STREAMS.subscriptionCost.formula,
        },
        {
          id: "Implementation services",
          label: "Implementation services",
          value: "$40K",
          cadence: "one-time",
          formula: PARTIAL_STREAMS.implementationServices.formula,
        },
      ],
      knownInputs: [
        { id: "gross-annual-benefit", label: "Annual benefit", value: "$279K" },
        { id: "total-annual-cost", label: "Annual cost", value: "$75K" },
      ],
      // §5 #14 — no authoritative aggregate formula node exists.
      formula: null,
    },
  },
  netSavings: {
    id: "netSavings",
    kind: "figure",
    label: "Net savings",
    value: null,
    unit: null,
    description: "Total rollout-adjusted benefit minus all program costs.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: null,
    aggregate: {
      horizonLabel: "3 years",
      horizonAssumed: true,
      contributors: [],
      knownInputs: [
        { id: "gross-annual-benefit", label: "Annual benefit", value: "$279K" },
        { id: "total-annual-cost", label: "Annual cost", value: "$75K" },
      ],
      formula: null,
    },
  },
  payback: {
    id: "payback",
    kind: "figure",
    label: "Payback period",
    value: null,
    unit: null,
    description: "Time until cumulative benefit covers cumulative cost.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: null,
    aggregate: {
      horizonLabel: "3 years",
      horizonAssumed: true,
      contributors: [],
      knownInputs: [],
      formula: null,
    },
  },
  totalCost: {
    id: "totalCost",
    kind: "figure",
    label: "Total cost",
    value: null,
    unit: null,
    description:
      "Subscription and implementation across the evaluation window.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: null,
    aggregate: {
      horizonLabel: "3 years",
      horizonAssumed: true,
      contributors: [
        {
          id: "Platform subscription",
          label: "Platform subscription",
          value: "$75K",
          cadence: "annual",
          formula: PARTIAL_STREAMS.subscriptionCost.formula,
        },
        {
          id: "Implementation services",
          label: "Implementation services",
          value: "$40K",
          cadence: "one-time",
          formula: PARTIAL_STREAMS.implementationServices.formula,
        },
      ],
      knownInputs: [
        { id: "total-annual-cost", label: "Annual cost", value: "$75K" },
      ],
      formula: null,
    },
  },

  /* ---- metrics ---- */
  "metric-overtime-hours": {
    id: "metric-overtime-hours",
    kind: "metric",
    label: "Nursing overtime hours",
    value: "$225K",
    unit: "per year",
    description: "Incidental overtime hours worked by nursing staff each year.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: null,
    aggregate: {
      horizonLabel: "3 years",
      horizonAssumed: true,
      contributors: [
        {
          id: "Overtime savings",
          label: "Overtime savings",
          value: "$225K",
          cadence: "annual",
          formula: PARTIAL_STREAMS.overtimeSavings.formula,
        },
      ],
      knownInputs: [
        {
          id: "Annual nursing overtime hours baseline",
          label: "Annual nursing overtime hours baseline",
          value: "6,000",
        },
      ],
      formula: null,
    },
  },
  "metric-documentation-time": {
    id: "metric-documentation-time",
    kind: "metric",
    label: "Documentation time per shift",
    value: "$12K",
    unit: "per year",
    description: "Time nurses spend charting during each shift.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: null,
    aggregate: {
      horizonLabel: "3 years",
      horizonAssumed: true,
      contributors: [
        {
          id: "Documentation time returned",
          label: "Documentation time returned",
          value: "$12K",
          cadence: "annual",
          formula: PARTIAL_STREAMS.documentationTimeReturned.formula,
        },
      ],
      knownInputs: [
        { id: "Shifts per year", label: "Shifts per year", value: "156" },
      ],
      formula: null,
    },
  },
  "metric-nursing-turnover": {
    id: "metric-nursing-turnover",
    kind: "metric",
    label: "Nursing turnover rate",
    value: "$42K",
    unit: "per year",
    description: "Share of nursing staff who leave each year.",
    provenance: null,
    sources: [],
    snippet: null,
    history: [],
    formula: null,
    aggregate: {
      horizonLabel: "3 years",
      horizonAssumed: true,
      contributors: [
        {
          id: "Turnover avoidance",
          label: "Turnover avoidance",
          value: "$42K",
          cadence: "annual",
          formula: PARTIAL_STREAMS.turnoverAvoidance.formula,
        },
      ],
      knownInputs: [
        { id: "Nurses in scope", label: "Nurses in scope", value: "400" },
      ],
      formula: null,
    },
  },

  /* ---- streams ---- */
  "Overtime savings": streamNode(PARTIAL_STREAMS.overtimeSavings),
  "Documentation time returned": streamNode(
    PARTIAL_STREAMS.documentationTimeReturned,
  ),
  "Turnover avoidance": streamNode(PARTIAL_STREAMS.turnoverAvoidance),
  "Platform subscription": streamNode(PARTIAL_STREAMS.subscriptionCost),
  "Implementation services": streamNode(PARTIAL_STREAMS.implementationServices),

  /* ---- facts ---- */
  "Annual nursing overtime hours baseline": {
    id: "Annual nursing overtime hours baseline",
    kind: "fact",
    label: "Annual nursing overtime hours baseline",
    value: "6,000",
    unit: "hours per year",
    description: "Incidental overtime hours nursing works today.",
    provenance: "provided",
    sources: [ORG_PROFILE_PAYROLL],
    snippet: null,
    // §5 #16 — no history is exposed on read-model values today.
    history: [],
    formula: null,
    aggregate: null,
  },
  "Overtime hourly rate": {
    id: "Overtime hourly rate",
    kind: "fact",
    label: "Overtime hourly rate",
    value: "$75",
    unit: "per hour",
    description: "What one overtime hour costs, loaded.",
    provenance: "provided",
    sources: [ORG_PROFILE_PAYROLL],
    snippet: null,
    history: [],
    formula: null,
    aggregate: null,
  },
  "Shifts per year": {
    id: "Shifts per year",
    kind: "fact",
    label: "Shifts per year",
    value: "156",
    unit: "shifts per year",
    description: "How many nursing shifts run in a year.",
    provenance: "provided",
    sources: [ORG_PROFILE_SCHEDULE],
    snippet: null,
    history: [],
    formula: null,
    aggregate: null,
  },
  "Value of nursing hours": {
    id: "Value of nursing hours",
    kind: "fact",
    label: "Value of nursing hours",
    value: "$50",
    unit: "per hour",
    description: "What one regular nursing hour is worth, loaded.",
    provenance: "provided",
    sources: [ORG_PROFILE_PAYROLL],
    snippet: null,
    history: [],
    formula: null,
    aggregate: null,
  },
  "Nurses in scope": {
    id: "Nurses in scope",
    kind: "fact",
    label: "Nurses in scope",
    value: "400",
    unit: "nurses",
    description: "Nursing staff inside the deployment scope.",
    provenance: "provided",
    sources: [ORG_PROFILE_SCOPE],
    snippet: null,
    history: [],
    formula: null,
    aggregate: null,
  },

  /* ---- assumptions ---- */
  "Expected reduction in overtime": {
    id: "Expected reduction in overtime",
    kind: "assumption",
    label: "Expected reduction in overtime",
    value: "50%",
    unit: "of hours",
    description: "How much of the addressable overtime the product removes.",
    provenance: "estimated",
    sources: [
      {
        ...AIVA_CASE_STUDY,
        blurb:
          "Incidental overtime on the pilot unit fell roughly 50% within three months of go-live.",
      },
      ELION_BENCHMARK,
    ],
    snippet:
      "Incidental overtime on the pilot unit fell roughly 50% within three months of go-live.",
    history: [],
    formula: null,
    aggregate: null,
  },
  "Hours saved per shift": {
    id: "Hours saved per shift",
    kind: "assumption",
    label: "Hours saved per shift",
    value: "1.5",
    unit: "hours per shift",
    description: "Charting time returned to a nurse on each shift.",
    provenance: "estimated",
    sources: [
      {
        ...AIVA_CASE_STUDY,
        blurb:
          "Nurses reported roughly 1.5 hours per shift returned to direct patient care after ambient documentation was deployed.",
      },
    ],
    snippet:
      "Nurses reported roughly 1.5 hours per shift returned to direct patient care after ambient documentation was deployed.",
    history: [],
    formula: null,
    aggregate: null,
  },
  "Annual nursing turnover rate": {
    id: "Annual nursing turnover rate",
    kind: "assumption",
    label: "Annual nursing turnover rate",
    value: "21%",
    unit: "of nurses",
    description: "Share of nursing staff who leave in a year.",
    provenance: "estimated",
    sources: [NSI_RETENTION_REPORT],
    snippet:
      "The average hospital turned over 20.7% of its bedside nursing staff in the reporting year.",
    history: [],
    formula: null,
    aggregate: null,
  },
  "Cost to replace one nurse": {
    id: "Cost to replace one nurse",
    kind: "assumption",
    label: "Cost to replace one nurse",
    value: "$50K",
    unit: "per nurse",
    description: "What it costs to recruit and ramp one replacement nurse.",
    provenance: "estimated",
    sources: [NSI_RETENTION_REPORT],
    snippet:
      "The average cost of turnover for one bedside RN is $56,300, with recruiting, orientation, and lost productivity making up the bulk.",
    history: [],
    formula: null,
    aggregate: null,
  },
  "Improvement in nursing turnover": {
    id: "Improvement in nursing turnover",
    kind: "assumption",
    label: "Improvement in nursing turnover",
    value: "1%",
    unit: "of turnover cost",
    description: "How much annual nursing turnover improves.",
    provenance: "estimated",
    sources: [NSI_RETENTION_REPORT],
    snippet: null,
    history: [],
    formula: null,
    aggregate: null,
  },
  "Annual subscription fee": {
    id: "Annual subscription fee",
    kind: "assumption",
    label: "Annual subscription fee",
    value: "$75K",
    unit: "per year",
    description: "Recurring subscription fee for each year of the model.",
    provenance: "provided",
    sources: [AIVA_PRICING_QUOTE],
    snippet: null,
    history: [],
    formula: null,
    aggregate: null,
  },
  "Implementation services fee": {
    id: "Implementation services fee",
    kind: "assumption",
    label: "Implementation services fee",
    value: "$40K",
    unit: "one time",
    description: "Front-loaded integration and enablement work.",
    provenance: "provided",
    sources: [AIVA_PRICING_QUOTE],
    snippet: null,
    history: [],
    formula: null,
    aggregate: null,
  },
};

/**
 * The bibliography as the mapper actually derives it from per-value evidence.
 *
 * Every difference from `COMPUTED_SOURCES` is a real limit of the data, not a
 * shortcut:
 *
 * - **No publication year.** Nothing in the stack records one, so `meta` is
 *   the URL host and the source class alone.
 * - **No trust note.** No generation step writes one this pass.
 * - The second entry has no name of its own — `EvidenceReference` carries only
 *   `citedText` — so its title is the first sentence of the quote, truncated.
 * - The third is internal, so it renders "System of record: …" rather than a
 *   link, from the sanitized `dedupKey`.
 *
 * Nothing cites them: no claim carries a source identifier until the exec
 * summary and risk register are generated, so all three sort as uncited in
 * read-model order and every `citation` below stays `null`. An uncited source
 * still ships — it is part of the evidence base the report rests on, and a
 * bibliography that silently drops sources understates that.
 */
const PARTIAL_SOURCES: ReportSource[] = [
  {
    n: 1,
    dedupKey: "aiva-health-cedars-case-study",
    title: "Aiva Health · Cedars deployment case study",
    meta: "aivahealth.com · external research",
    context:
      "Incidental overtime on the pilot unit fell roughly 50% within three months of go-live.",
    trust: null,
    urls: ["https://aivahealth.com/case-studies/cedars"],
    internalLabel: null,
    backsCount: 2,
  },
  {
    n: 2,
    dedupKey: "nsi-2025-retention-report",
    title:
      "Replacing a single staff nurse costs a typical hospital about $56,300.",
    meta: "nsinursingsolutions.com · external research",
    context:
      "Replacing a single staff nurse costs a typical hospital about $56,300. The 2025 figure is a national average across reporting systems.",
    trust: null,
    urls: [
      "https://www.nsinursingsolutions.com/documents/library/nsi_national_health_care_retention_report.pdf",
    ],
    internalLabel: null,
    backsCount: 1,
  },
  {
    n: 3,
    dedupKey: "nursing_workforce_plan_fy26",
    title: "nursing workforce plan fy26",
    meta: "internal source",
    context:
      "Documentation burden and incidental overtime are named as the two retention priorities for the coming cycle.",
    trust: null,
    urls: [],
    internalLabel: "nursing workforce plan fy26",
    backsCount: 1,
  },
];

/**
 * Only what today's read model supplies. The horizon is the model's 36-month
 * fallback rather than an explicit assumption, so it renders labelled as
 * assumed (§5 #1, plan §6 risk 1). Every other §5 gap is visibly absent: net
 * savings, payback and total cost (#2–#4); every metric shift
 * (#5); the problem detail (#6); highlights and risks (#7, #8); the scope
 * prose and attribution (#9, #10); aggregate formulas (#14); and value history
 * (#16). §5 gap #13 is closed — the bibliography is derived from per-value
 * evidence (`PARTIAL_SOURCES`) — but every `citation` is still `null`, because
 * no claim carries a source identifier to resolve yet.
 *
 * Product selection (#11) is no longer a gap: the rationale rows come from the
 * generated product-selection artifact. Comparable vendors (#12) still are —
 * nothing models that card list.
 *
 * Scope *tiles* are no longer a gap either: deployment context is captured at
 * intake and organization-wide counts come off the org profile, so this fixture
 * carries both — qualitative deployment facts and one org-wide count that is
 * labelled as such and stays out of the pill's one-line value.
 */
export const partialReportFixture: ReportData = {
  locked: false,
  needsInputCount: 0,
  projectionStatus: "computed",
  problem: {
    statement:
      "Nursing documentation burden is driving avoidable incidental overtime and contributing to turnover on inpatient units.",
    detail: null,
    citation: null,
  },
  figures: {
    ready: true,
    // The model defaults to 36 months when no `contract_term_years`
    // assumption exists, so real data has a horizon — just not an explicit
    // one. Labelled assumed per plan §6 risk 1.
    horizonYears: 3,
    horizonAssumed: true,
    roiPercent: 160,
    netSavings: null,
    paybackLabel: null,
    totalCost: null,
  },
  // Benefits only, as `toReportData` builds it: cost groups reach `traceNodes`
  // and the `totalCost` figure, never the rendered metric list. A cost row here
  // would model a state the mapper cannot produce — and would win a
  // `topBenefit` ranking that is supposed to see benefits alone.
  metrics: [
    {
      id: "metric-overtime-hours",
      traceNodeId: "metric-overtime-hours",
      label: "Nursing overtime hours",
      description:
        "Incidental overtime hours worked by nursing staff each year.",
      amount: "$225K",
      amountValue: 225_000,
      cadence: "annual",
      shift: null,
      streams: [PARTIAL_STREAMS.overtimeSavings],
    },
    {
      id: "metric-documentation-time",
      traceNodeId: "metric-documentation-time",
      label: "Documentation time per shift",
      description: "Time nurses spend charting during each shift.",
      amount: "$12K",
      amountValue: 12_000,
      cadence: "annual",
      shift: null,
      streams: [PARTIAL_STREAMS.documentationTimeReturned],
    },
    {
      id: "metric-nursing-turnover",
      traceNodeId: "metric-nursing-turnover",
      label: "Nursing turnover rate",
      description: "Share of nursing staff who leave each year.",
      amount: "$42K",
      amountValue: 42_000,
      cadence: "annual",
      shift: null,
      streams: [PARTIAL_STREAMS.turnoverAvoidance],
    },
  ],
  leafNodeKeys: {},
  execSummary: [],
  highlights: [],
  risks: [],
  scope: {
    // Real deployment context, exactly as `intakeContext()` projects it:
    // qualitative label/value pairs in intake-definition order, no numbers.
    // Only the allowlisted ones reach the pill line, which reads
    // "Nursing · Inpatient hospital"; "Function class" would render
    // "Clinical function class" and is curated out of the line but kept in
    // the popover.
    tiles: [
      {
        id: "deployment:function-department",
        label: "Function / department",
        value: "Nursing",
        origin: "deployment",
        inPill: true,
      },
      {
        id: "deployment:function-class",
        label: "Function class",
        value: "Clinical",
        origin: "deployment",
        inPill: false,
      },
      {
        id: "deployment:care-setting",
        label: "Care setting",
        value: "Inpatient hospital",
        origin: "deployment",
        inPill: true,
      },
      // Org-wide, from the organizational profile. Never in the pill line —
      // it is true of the system, not of this deployment.
      {
        id: "org:bedCount",
        label: "Licensed Beds",
        value: "200",
        origin: "organization",
        inPill: false,
      },
    ],
    outOfScope: null,
    attribution: null,
  },
  // §5 #11 — the two rows the product-selection artifact backs. The
  // brief-backed "Why now" row is the fallback this fixture deliberately does
  // not exercise; `computedReportFixture` covers it.
  rationale: [
    {
      id: "why-vendor",
      label: "Why this vendor",
      body: "Aiva Health is an ambient documentation product for inpatient nursing shift charting, the workflow this opportunity models, and its pricing is per deployment rather than per clinician.",
      citation: null,
    },
    {
      id: "why-fits",
      label: "Why it fits this organization",
      body: "The benefit rests on incidental overtime, which this system already tracks, so the primary driver is auditable against payroll after go-live.",
      citation: null,
    },
  ],
  comparableVendors: [],
  sources: PARTIAL_SOURCES,
  traceNodes: PARTIAL_TRACE_NODES,
};
